import { useGlobalConfig } from 'context/GlobalContext';
import userRequired from 'hoc/userRequired';
import React, { useEffect, useRef } from 'react';
import { renderToString } from 'react-dom/server';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { Platform } from 'utils/platform';

import { VideoInfoView } from '../Video/UI/Info';
import { usePlayer } from '../context';

declare global {
  interface Window {
    SpallaPlayer: any;
  }
}

type SpallaPlayerWebProps = {
  program?: CmsProgram;
  streamId?: string;
  videoId?: string;
  autoplay?: boolean;
  muted?: boolean;
  isLive?: boolean;
};

export type SpallaPlayerInstance = {
  on: (event: string, callback: (e: any) => void) => void;
  postMessage?: (message: any) => void;
  play?: () => void;
  pause?: () => void;
  mute?: () => void;
  unmute?: () => void;
  hideSubtitles?: () => void;
  setActiveSubtitle?: (language: string) => void;
  setVolume?: (volume: number) => void;
  playbackRate?: (rate: number) => void;
  currentTime?: (seconds: number) => void;
  forward?: (seconds: number) => void;
  rewind?: (seconds: number) => void;
  bookmarks?: (
    bookmarks: Array<{
      start: number;
      end?: number;
      title?: string;
      color?: string;
    }>
  ) => void;
};

const SpallaPlayerWeb: React.FC<SpallaPlayerWebProps> = ({
  streamId,
  videoId,
  muted = false,
  isLive = false,
  program,
}) => {
  const playerRef = useRef<HTMLDivElement | null>(null);
  const spallaPlayerRef = useRef<SpallaPlayerInstance | null>(null);
  const { play, pause, isFullScreen } = usePlayer();
  const { isScrollingDown } = useGlobalConfig();
  const [isHovered, setIsHovered] = React.useState(false);
  const analytics = useAnalytics();

  const getSpallaPlayer = () => {
    return spallaPlayerRef.current;
  };

  const playerControls = {
    play: () => {
      const player = getSpallaPlayer();
      if (player?.play) {
        player.play();
        play();
      }
    },
    pause: () => {
      const player = getSpallaPlayer();
      if (player?.pause) {
        player.pause();
        pause();
      }
    },
    mute: () => {
      const player = getSpallaPlayer();
      if (player?.mute) {
        player.mute();
      }
    },
    unmute: () => {
      const player = getSpallaPlayer();
      if (player?.unmute) {
        player.unmute();
      }
    },
    setPlaybackRate: (rate: number) => {
      const player = getSpallaPlayer();
      if (player?.playbackRate) {
        player.playbackRate(Number(rate));
      }
    },
    seekTo: (seconds: number) => {
      const player = getSpallaPlayer();
      if (player?.currentTime) {
        player.currentTime(Number(seconds));
      }
    },
    forward: (seconds: number) => {
      const player = getSpallaPlayer();
      if (player?.forward) {
        player.forward(Number(seconds));
      }
    },
    rewind: (seconds: number) => {
      const player = getSpallaPlayer();
      if (player?.rewind) {
        player.rewind(Number(seconds));
      }
    },
    setVolume: (volume: number) => {
      const player = getSpallaPlayer();
      if (player?.setVolume) {
        player.setVolume(Number(volume));
      }
    },
    setActiveSubtitle: (language: string) => {
      const player = getSpallaPlayer();
      if (player?.setActiveSubtitle) {
        player.setActiveSubtitle(language);
      }
    },
    hideSubtitles: () => {
      const player = getSpallaPlayer();
      if (player?.hideSubtitles) {
        player.hideSubtitles();
      }
    },
  };

  useEffect(() => {
    if (Platform.OS === 'web' && !Platform.isTV) {
      if (isScrollingDown) {
        playerControls.pause();
        pause();
      } else {
        playerControls.play();
        play();
      }
    }
  }, [isScrollingDown]);

  useEffect(() => {
    if (!playerRef.current) return;

    const spallaPlayer = new window.SpallaPlayer('spalla-player', {
      liveId: isLive ? streamId : undefined,
      videoId: isLive ? undefined : videoId,
      width: '100%',
      height: '100%',
      autoplay: isLive,
      messages: true,
      bar: false,
      pip: false,
      search: false,
      muted: muted,
      audioplayer: false,
      offline: false,
      t: 20,
      subtitlePosition: 'inner',
      subtitleLanguage: 'es',
      aid: '11111111-2222-4bb6-4444-000000000000',
      info: true,
      resume: false,
      enableLikes: false,
      enableCc: false,
      live: isLive,
      playbackSpeeds: ['-'],
      controls: ['play', 'pause', 'fullscreen', 'volume', 'quality', 'live'],
      bookmarks: [
        { start: 10, end: 20, title: 'Bookmark 1', color: '#FF0000' },
        { start: 30, end: 70, color: '#00FF00' },
        { start: 50, end: 95, color: '#0000FF' },
        { start: 120, color: '#FF0000' },
        { start: 150 },
      ],
    });

    spallaPlayerRef.current = spallaPlayer;

    spallaPlayer.on('*', (e: any) => {
      if (e.event === 'ready') {
        play();
      }
      if (e.event === 'pause') {
        analytics.triggerMediaPauseActionEvent({
          programId: program.id,
          programTitle: program.title,
          programAgeRating: program.rating,
          programAdUnit: program.ads?.adunit?.val!,
          programCategory: program.category?.map((tag: any) => tag.name).join(', '),
          programDirector: program.director,
          programGenre: program.genre.map((genre: any) => genre.name).join(', '),
          programYear: program.year,
        });
        pause();
      }
      if (e.event === 'play') {
        analytics.triggerMediaPlayActionEvent({
          programId: program.id,
          programTitle: program.title,
          programAgeRating: program.rating,
          programAdUnit: program.ads?.adunit?.val!,
          programCategory: program.category?.map((tag: any) => tag.name).join(', '),
          programDirector: program.director,
          programGenre: program.genre.map((genre: any) => genre.name).join(', '),
          programYear: program.year,
        });
        play();
      }

      if (e.event === 'error') {
        console.error('SpallaPlayer error', e);
      }
    });

    return () => {
      spallaPlayerRef.current = null;
    };
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        paddingTop: '56.25%',
        backgroundColor: '#000',
      }}>
      <div
        ref={playerRef}
        style={{
          position: 'absolute',
          width: '100% ',
          height: '100%',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
        }}
        id="spalla-player"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />
      <div
        className="theo-top-controlbar"
        style={{
          position: isFullScreen ? 'fixed' : 'absolute',
          top: 0,
          left: 0,
          right: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
          padding: 20,
          color: 'white',
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          zIndex: 9999,
          transition: 'opacity 0.3s ease',
          opacity: isHovered ? 1 : 0,
          pointerEvents: 'none',
          width: 'inherit',
        }}
        dangerouslySetInnerHTML={{
          __html: renderToString(
            <VideoInfoView
              title={program?.title || ''}
              image={[program?.imageVertical?.url || '', program?.imageHorizontal?.url || '']}
              programId={program?.id || ''}
              programUrl={program?.url || ''}
              ratingDescription={program?.rateDescription || ''}
              ratingValue={program?.rating || ''}
              videoTracks={[]}
            />
          ),
        }}
      />
    </div>
  );
};

export default userRequired(SpallaPlayerWeb);
